import PropTypes from "prop-types";
import React from "react";

import { useDeviceInfo } from "@swa-ui/browser";
import { Heading, Image, ListMatrix } from "@swa-ui/core";

import { backgroundPropType, getBackgroundStyle } from "../Banner/BannerUtils";
import { CallToAction } from "../CallToAction";
import styles from "./BannerGridLayout2.module.scss";
import { callToActionPropType } from "./utils";

const BannerGridLayout2 = (props) => {
  const { background, callToAction, items, title } = props;
  const { screenSize } = useDeviceInfo();

  return (
    <div style={getBackgroundStyle(background)}>
      {renderHeading()}
      <ListMatrix {...getListMatrixProps()} />
      {callToAction && <div className={styles.ctaContainer}>{renderCallToAction()}</div>}
    </div>
  );

  function renderHeading() {
    return (
      title && (
        <div {...getHeaderContainerProps()}>
          <Heading {...getHeadingProps()} />
        </div>
      )
    );
  }

  function renderCallToAction() {
    return <CallToAction {...callToAction} />;
  }

  function getHeaderContainerProps() {
    return {
      className: styles.headerContainer,
    };
  }

  function getHeadingProps() {
    return {
      children: <span {...getStyleProps(title)}>{title.value}</span>,
      headingLevel: 4,
      styleLevel: 3,
    };
  }
  function getStyleProps(styleProps) {
    const { color, fontSize, fontWeight } = styleProps ?? {};

    return {
      style: {
        color,
        fontSize,
        fontWeight,
      },
    };
  }

  function getListMatrixProps() {
    const columnSizeMap = {
      large: 4,
      medium: 3,
      small: 1,
      xlarge: 4,
    };
    const itemsLength = items.length;
    const maxSize = columnSizeMap[screenSize];
    const fitsInGrid = itemsLength % maxSize === 0;

    return {
      itemClassName: styles.item,
      items: getItems(),
      layout: fitsInGrid ? "grid" : "none",
      numberOfColumns: itemsLength > maxSize ? maxSize : itemsLength,
      rowClassName: styles.row,
    };
  }

  function getItems() {
    return items.map((item) => {
      const { altText, height, path, width } = item?.image ?? {};
      const imageProps = {
        alt: altText,
        fitToContainer: true,
        height,
        src: `${path}`,
        width,
      };

      return {
        content: [
          <div key={path} className={styles.bannerImage}>
            <Image {...imageProps} />
          </div>,
        ],
      };
    });
  }
};

BannerGridLayout2.propTypes = {
  /** Background configuration. */
  background: backgroundPropType,

  /** CallToAction link/button to be displayed. */
  callToAction: callToActionPropType,

  /** Array of images to display. */
  items: PropTypes.arrayOf(
    PropTypes.shape({
      image: PropTypes.shape({
        altText: PropTypes.string,
        path: PropTypes.string,
      }),
    })
  ).isRequired,

  /** Optional title for the banner. */
  title: PropTypes.shape({
    value: PropTypes.string,
  }),
};

export default BannerGridLayout2;
